import { AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { CalculationBasedType, Campaign, CampaignAdminRequest, CampaignLocationFilter, CampaignTargetUser, CampaignsCampaignAdminApiClient, CampaignsCampaignLocationFilterAdminApiClient, CampaignsOutboundEventConfigurationApiClient, ConsumptionValidationToolEnum, PunchCardFlow, PunchCardReceiptToolType, PunchCardStrategy, PunchCardStrategyType } from 'src/app/api/api-campaigns.service.generated';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { CampaignBaseEditComponent } from '../_/campaign-base-edit/campaign-base-edit.component';
import { CampaignLocationFiltersComponent } from '../_/campaign-location-filters/campaign-location-filters.component';
import { ConsumerReferralTabComponent } from './consumer-referral-tab/consumer-referral-tab.component';
import { PunchCardStrategyFlowComponent } from './punch-card-strategy-flow/punch-card-strategy-flow.component';
import { PunchCardStrategyReceiptComponent } from './punch-card-strategy-receipt/punch-card-strategy-receipt.component';
import { PunchCardStrategyComponent } from './punch-card-strategy/punch-card-strategy.component';
import { StaffReferralTabComponent } from './staff-referral-tab/staff-referral-tab.component';
import { BannerImage } from '../_/campaign-banner-image/campaign-banner-image.component';
import { CampaignBannerComponent } from '../_/campaign-banner/campaign-banner.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PunchCardCampaignService } from './punch-card-campaign-edit.service';
import { Currencies } from './punch-card-strategy/lib/currencies';
import { TargetRoleTabComponent } from './target-role-tab/target-role-tab.component';
import { PERMISSION } from 'src/app/const/permission';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { FlexLayoutModule } from '@angular/flex-layout';
import { CommonModule } from '@angular/common';
import { CampaignMediaComponent } from '../_/campaign-media/campaign-media.component';
import { CampaignOutboundEventsComponent } from '../campaign-outbound-events/campaign-outbound-events.component';
import { PageContainerComponent, ButtonComponent, BreadcrumbsComponent, LoadingOverlayComponent } from 'wecheer-common';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { MatTabsModule } from "@angular/material/tabs";
import { MatStepperModule } from '@angular/material/stepper';
import { ScreenService } from 'src/app/common/services/screen.service';
import { UserPermissionsService } from 'src/app/common/services/user-permissions.service';

@Component({
  selector: 'app-punch-card-campaign-edit',
  templateUrl: './punch-card-campaign-edit.component.html',
  styleUrls: ['./punch-card-campaign-edit.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    MatTabsModule,
    MatStepperModule,
    FlexLayoutModule,
    CampaignBaseEditComponent,
    CampaignLocationFiltersComponent,
    CampaignOutboundEventsComponent,
    ConsumerReferralTabComponent,
    PunchCardStrategyFlowComponent,
    PunchCardStrategyReceiptComponent,
    PunchCardStrategyComponent,
    StaffReferralTabComponent,
    CampaignBannerComponent,
    TargetRoleTabComponent,
    CampaignMediaComponent,
    HasAnyPermissionDirective,
    PageContainerComponent,
    ButtonComponent,
    BreadcrumbsComponent,
    LoadingOverlayComponent
  ]
})
export class PunchCardCampaignEditComponent implements OnInit, AfterContentChecked, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private campaignLocationFiltersApi = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private outboundEventsApi = inject(CampaignsOutboundEventConfigurationApiClient);
  punchCardCampaignService = inject(PunchCardCampaignService);
  private userPermissionsService = inject(UserPermissionsService);
  appVersion = inject(AppVersionService);
  screen = inject(ScreenService);

  readonly campaignBase = viewChild<CampaignBaseEditComponent>('campaignBase');
  readonly campaignBanner = viewChild<CampaignBannerComponent>('campaignBanner');
  readonly campaignStrategy = viewChild<PunchCardStrategyComponent>('campaignStrategy');
  readonly campaignStrategyReceipt = viewChild<PunchCardStrategyReceiptComponent>('campaignStrategyReceipt');
  readonly campaignStrategyFlow = viewChild<PunchCardStrategyFlowComponent>('campaignStrategyFlow');
  readonly campaignLocationFilters = viewChild<CampaignLocationFiltersComponent>('campaignLocationFilters');
  readonly consumerReferralTab = viewChild<ConsumerReferralTabComponent>('consumerReferralTab');
  readonly staffReferralTab = viewChild<StaffReferralTabComponent>('staffReferralTab');
  readonly campaignTargetRole = viewChild<TargetRoleTabComponent>('campaignTargetRole');
  readonly campaignOutboundEvents = viewChild<CampaignOutboundEventsComponent>('campaignOutboundEvents');

  editModel: CampaignAdminRequest;

  campaign: Campaign;

  campaignReplay = new BehaviorSubject<Campaign>(undefined);

  campaignId: string;
  originalCampaignId: string;
  advertiserId: string;
  isCloning: boolean;

  isLoading: boolean;
  isAdding: boolean;

  strategyType: PunchCardStrategyType;
  captureToolType: string;

  baseCalculation: CalculationBasedType;
  currencyCode: string;

  isContactSync: boolean;

  isDataConsumerSync: boolean;

  targetRole: CampaignTargetUser;

  bannerDuration: number;
  isBrandAdmin = false;
  readonly permissions = PERMISSION;

  _viewMode: 'target-role' | 'receipt' | 'scheme' | 'flow' | 'locations' | 'consumer-referral' | 'staff-referral' | 'media' | 'banner' | 'json' | '' = 'target-role';
  public get viewMode() {
    return this._viewMode;
  }
  public set viewMode(value: any) {
    this._viewMode = value;
    this.punchCardCampaignService.viewMode = value;
  }
  allowFlow: boolean;     // triggerd from Tiers tab to update validators on depending tabs
  onAllowFlowChange(allowFlow: boolean) {
    this.allowFlow = allowFlow;
    this.punchCardCampaignService.allowFlow = this.allowFlow;
  }

  constructor() {
    const cloneParam = this.route.snapshot.queryParams['clone'];
    this.isCloning = cloneParam === 'true' || cloneParam === true || cloneParam === '1' || cloneParam === 1;
    this.isAdding = this.route.snapshot.params.campaignId === 'new' || this.isCloning;
    this.originalCampaignId = this.route.snapshot.params.campaignId;
    this.campaignId = this.isAdding ? undefined : this.route.snapshot.params.campaignId;
    this.advertiserId = this.route.snapshot.params.advertiserId;
  }

  ngOnDestroy(): void {
    this.punchCardCampaignService.resetData();
  }

  ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  async ngOnInit() {
    this.getProductRewards();
    await this.initData();
    this.getProductListWithFilter(this.campaign?.strategy?.receiptToolTypeIds || []);
    this.punchCardCampaignService.viewMode = this.viewMode;
    this.userPermissionsService.hasPermission(PERMISSION.BRANDADMIN).subscribe(isBrandAdmin => this.isBrandAdmin = isBrandAdmin);
  }

  async initData() {
    if (this.isCloning) {
      await this.cloneCampaign();
    } else {
      this.campaign = this.campaignId
        ? await lastValueFrom(this.campaignsApi.getCampaign(this.campaignId))
        : new Campaign({ advertiserId: this.advertiserId });

      const clf = this.campaignId
        ? await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.campaignId))
        : new CampaignLocationFilter({ campaignId: this.campaignId, filters: [] });
      this.campaignLocationFilters().setFormValue(clf);
    }

    this.campaignReplay.next(this.campaign);
    this.syncBanners();

    if (!this.isAdding || this.isCloning) {
      const targetRole = this.campaign?.targetUser || CampaignTargetUser.Consumer;
      this.strategyType = this.campaign.strategy?.strategyType || '';
      this.captureToolType = this.campaign.strategy?.receiptToolTypeId;
      this.baseCalculation = this.campaign.strategy?.calculationBaseType || CalculationBasedType.QuantityBased;
      this.currencyCode = this.campaign.strategy?.currencyCode || Currencies[0].value;
      this.targetRole = targetRole;

      // Use current campaign data for form pre-population (works for both editing and cloning)
      this.punchCardCampaignService.setSchemeList(this.campaign.strategy?.schemes);
      this.punchCardCampaignService.captureToolType = this.campaign.strategy?.receiptToolTypeId;
      this.punchCardCampaignService.allowFlow = !!this.campaign.strategy?.flow;
      this.punchCardCampaignService.strategyType = this.strategyType;
      this.punchCardCampaignService.targetRole = this.campaign?.targetUser;



      this.campaignTargetRole().targetRole = targetRole;
      await this.onTargetRoleChange(targetRole);

      if (this.isCloning) {
        setTimeout(() => {
          this.campaignStrategyFlow()?.forceRefresh();
        }, 100);
      }
    } else {
      this.baseCalculation = CalculationBasedType.QuantityBased;
      this.strategyType = undefined;
      this.currencyCode = Currencies[0].value;
    }

    this.isContactSync = this.campaign.synchronizationContactGLO;
    this.isDataConsumerSync = this.campaign.synchronizationDataConsumerGLO;

    this.bannerDuration = this.campaign.bannerDuration;
  }

  private async cloneCampaign() {
    const originalCampaign = await lastValueFrom(this.campaignsApi.getCampaign(this.originalCampaignId));

    // Copy all data from original campaign, reset only essential fields
    this.campaign = new Campaign({
      ...originalCampaign,
      id: undefined,
      campaignId: undefined,
      dateUpdated: undefined,
      title: originalCampaign.title ? `${originalCampaign.title} (Copy)` : undefined,
      advertiserId: this.advertiserId
    });

    // Load related data
    const [clf, consumerReferralCampaigns, staffReferralCampaigns] = await Promise.all([
      lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.originalCampaignId)),
      lastValueFrom(this.campaignsApi.getConsumerReferralCampaigns(this.originalCampaignId)),
      lastValueFrom(this.campaignsApi.getStaffReferralCampaigns(this.originalCampaignId))
    ]);

    // Set form values
    this.campaignLocationFilters().setFormValue(clf);

    const clonedConsumerReferralCampaigns = consumerReferralCampaigns?.map(campaign => {
      const clonedCampaign = new Campaign({ ...campaign, id: undefined, campaignId: undefined });

      if (clonedCampaign.strategy && 'refCampaignId' in clonedCampaign.strategy) {
        clonedCampaign.strategy.refCampaignId = undefined;
      }
      return clonedCampaign;
    });

    const clonedStaffReferralCampaigns = staffReferralCampaigns?.map(campaign => {
      const clonedCampaign = new Campaign({ ...campaign, id: undefined, campaignId: undefined });

      if (clonedCampaign.strategy && 'refCampaignId' in clonedCampaign.strategy) {
        clonedCampaign.strategy.refCampaignId = undefined;
      }
      return clonedCampaign;
    });

    this.consumerReferralTab().setFormValue(clonedConsumerReferralCampaigns);
    this.staffReferralTab().setFormValue(clonedStaffReferralCampaigns);
  }

  getProductListWithFilter(cvts: ConsumptionValidationToolEnum[]) {
    this.punchCardCampaignService.getProductListWithFilter(cvts);
  }

  async getProductRewards() {
    this.punchCardCampaignService.advertiserId = this.advertiserId;
    this.isLoading = true;
    await this.punchCardCampaignService.getProductRewards();
    this.isLoading = false;

  }

  contactSyncChanged(event: any) {
    this.isContactSync = event;
  }

  dataConsumerSyncChanged(event: any) {
    this.isDataConsumerSync = event;
  }

  syncBanners() {
    // sync baseCampaign main banner with banner slider tab (can be edited from 2 places)
    this.campaignBase().mainBannerChanged.subscribe(banner => { this.campaignBanner()?.setMainBanner(banner); });
    this.campaignBanner()?.mainBannerChanged.subscribe(banner => { this.campaignBase().setMainBanner(banner); });

    if (this.campaign.strategy) {
      this.campaignBanner()?.setBanners(this.campaign.strategy.banner);
    }

    if (!this.campaign.strategy || !this.campaign.strategy.banner) {
      // old campaings have banner set on campaign.photoBannerUrl prop -> add it to the banner list
      if (this.campaign.photoBannerUrl)
        this.campaignBanner()?.setMainBanner(new BannerImage({ photoUrl: this.campaign.photoBannerUrl }));
    }
    else {
      // new campaign will have banner list -> set it also on the campaign base main banner
      const strategyBanner = this.campaign.strategy.banner;
      if (strategyBanner && strategyBanner.url1)
        this.campaignBase().setMainBanner(new BannerImage({ photoUrl: strategyBanner.url1 }))
    }
  }


  forceRefresh() {
    const c = this.campaign;
    this.campaign = null;
    this.isLoading = true;
    setTimeout(() => {
      this.campaign = c;
      this.isLoading = false;
    }, 500);
  }


  async onSave() {
    const request = this.campaignBase().getFormValue();
    if (!request) return;

    let strategy = this.campaignStrategy().getFormValue();

    // Validate early if using HandshakeQR to prevent unnecessary processing
    if (strategy?.receiptToolTypeId === PunchCardReceiptToolType.HandshakeQR && !this.campaignStrategy().isValidHandshakeQRScheme()) {
      return;
    }

    const campaignStrategyReceipt = this.campaignStrategyReceipt().getFormValue();
    const campaignStrategyFlow = this.campaignStrategyFlow().getFormValue();
    const campaignBanner = this.campaignBanner().getFormValue();

    const campaignBannerValue = this.campaignBanner();
    const campaignStrategyFlowValue = this.campaignStrategyFlow();
    const consumerReferralTab = this.consumerReferralTab();
    const staffReferralTab = this.staffReferralTab();
    const campaignOutboundEvents = this.campaignOutboundEvents();

    campaignOutboundEvents?.markAsTouched();

    if (
      !this.campaignStrategy().isValid() ||
      !this.campaignStrategyReceipt().isValid() ||
      !campaignStrategyFlowValue.isValid() ||
      !consumerReferralTab.isValid() ||
      !staffReferralTab.isValid() ||
      !this.campaignTargetRole().isValid() ||
      !campaignBannerValue.isValid() ||
      !campaignOutboundEvents.isValid()
    )
      return;              // update model with tab tiers data

    strategy = new PunchCardStrategy({
      ...strategy,
      ...campaignStrategyReceipt
    })

    if (campaignStrategyFlow instanceof PunchCardFlow) {
      strategy.flow = campaignStrategyFlow;
      strategy.restartLimit = campaignStrategyFlow.restartLimit;
    } else {
      if (strategy.schemes?.length > 1) {
        new MessageDialogModel(`
          Non-tier does not support multiple schemes. Please make sure you only add only one scheme for the campaign.
        `).show(this.dialog);
        return;
      }
      strategy.flow = undefined;
      if (campaignStrategyFlow) {
        strategy = new PunchCardStrategy({
          ...strategy,
          ...campaignStrategyFlow
        })
      }

    }

    request.campaignLocationFilter = this.campaignLocationFilters().getFormValue();  // update model with tab locations
    request.campaignId = this.campaignId;
    request.advertiserId = this.advertiserId;
    request.punchCardStrategy = strategy;
    this.campaignStrategyFlow().stampSetting();
    if (campaignStrategyFlow?.uploadKeyReward) {
      request.uploadKeyReward = campaignStrategyFlow.uploadKeyReward;
    }
    // request.banner = this.campaignBanner.getBanners();
    request.punchCardStrategy.banner = campaignBannerValue?.getBanners();

    strategy.allowDuplicates = request.allowDuplicates;                             // set this prop on campaign and also on strategy (to be fixed later)

    // attach consumer referral campaigns to the request
    request.consumerReferralCampaigns = consumerReferralTab.getFormConsumerReferralCampaigns();

    // attach staff referral campaigns to the request
    request.staffReferralCampaigns = this.staffReferralTab().getFormStaffReferralCampaigns();

    // attach target user role
    request.campaignTargetUser = this.targetRole;

    request.bannerDuration = campaignBanner?.bannerDuration;

    this.isLoading = true;
    try {
      const savedCampaign = await lastValueFrom(this.campaignsApi.createCampaign(request));

      // Save outbound events configuration after campaign is saved
      const outboundEventsConfig = campaignOutboundEvents?.getFormValue();
      if (outboundEventsConfig && savedCampaign?.campaignId) {
        try {
          await lastValueFrom(this.outboundEventsApi.updateCampaignConfig(savedCampaign.campaignId, outboundEventsConfig));
        } catch (err) {
          console.warn('Failed to save outbound events configuration:', err);
          // Don't block campaign save if outbound events fail
        }
      }

      this.snackBar.open(`Campaign ${request.title} was saved`, null, { duration: 3000 });
      this.router.navigate(['advertisers', this.advertiserId]);

    } catch (err: any) {
      if (err.status === 400 && err.response)
        new MessageDialogModel(err.response).show(this.dialog);
    } finally {
      this.isLoading = false;
    }
  }

  refAssignObj(ref: any, obj: any) {
    if (!ref) return;
    for (const key of Object.keys(ref))
      delete ref[key];

    if (!obj) return;
    for (const key of Object.keys(obj))
      ref[key] = obj[key];
  }

  onStrategyChange(strategyType) {
    this.strategyType = strategyType;
    this.punchCardCampaignService.strategyType = this.strategyType;
  }

  onBaseCalculationChange(baseCalculation) {
    this.baseCalculation = baseCalculation;
    this.punchCardCampaignService.baseCalculation = this.baseCalculation;
  }

  onCurrencyChange(currencyCode) {
    this.currencyCode = currencyCode;
    this.punchCardCampaignService.currencyCode = this.currencyCode;
  }

  onCaptureToolChange(e) {
    this.captureToolType = e;
    this.punchCardCampaignService.captureToolType = this.captureToolType;

    if (this.captureToolType) {
      this.punchCardCampaignService.captureToolTypeChanged.next(this.captureToolType);
    }

    this.campaignStrategyFlow()?.onRoleChange();
  }

  onSchemeChanged(schemes) {
    if (this.campaign?.strategy) {
      this.campaign.strategy.schemes = schemes;
    }
    this.punchCardCampaignService.setSchemeList(schemes);
  }

  async onTargetRoleChange(role) {
    this.targetRole = role;
    this.punchCardCampaignService.targetRole = this.targetRole;

    // wait for view to stabilize
    await new Promise(resolve => setTimeout(resolve, 0));

    // Update capture tool settings
    this.campaignStrategyReceipt().onRoleChange();

    // Update campaign flow settings
    this.campaignStrategyFlow().onRoleChange();

    // Update staff referral settings
    this.staffReferralTab().onRoleChange();

    // Load rewards for the new role
    await this.punchCardCampaignService.getProductRewards();
  }

  // Add getter to check if Staff target role is selected
  get isStaffSelected(): boolean {
    return this.targetRole === CampaignTargetUser.Staff;
  }

  // Check if Main Information tab (including all sub-tabs) is valid
  get isMainInformationValid(): boolean {
    // Check main campaign base form
    if (this.campaignBase()?.form?.valid === false) {
      return false;
    }

    // Check Strategy sub-tab
    if (this.campaignStrategy()?.isValid() === false) {
      return false;
    }

    // Check Target Roles sub-tab
    if (this.campaignTargetRole()?.isValid() === false) {
      return false;
    }

    // Check Capture Tools sub-tab
    if (this.campaignStrategyReceipt()?.isValid() === false) {
      return false;
    }

    // Check Tiers sub-tab
    if (this.campaignStrategyFlow()?.isValid() === false) {
      return false;
    }

    // Check Banners sub-tab
    if (this.campaignBanner()?.isValid() === false) {
      return false;
    }

    return true;
  }
}
